import { forwardRef, useRef, useImperativeHandle, type ForwardedRef } from 'react';

import { RichTextEditorProvider } from '../providers/rich-text-editor-provider';
import type { ControlsInput, ImageUploadFunction } from '../types';
import { useRteUi } from '../ui/ui-context';

export interface HeadlessRichTextEditorProps {
  /**
   * The controls of the rich text editor
   *
   * @default [['bold', 'italic', 'underline'], ['alignLeft', 'alignCenter', 'alignRight'], ['bulletList', 'numberList'], ['image', 'table', 'latex']]
   */
  controls?: ControlsInput;
  /**
   * The callback function when the editor is blurred
   */
  onBlur?: () => void;
  /**
   * The callback function when the editor is changed
   */
  onChange?: (value: string) => void;
  /**
   * If true, the editor's border will become red
   */
  error?: boolean;
  /**
   * The callback function when the image is uploaded
   */
  onImageUpload?: ImageUploadFunction;
  /**
   * If true, the editor will show a loading overlay
   */
  loading?: boolean;
  /**
   * The placeholder of the editor
   */
  placeholder?: string;
  /**
   * The value of the editor
   */
  value?: string;
  /**
   * The number of rows to control the height of the editor
   *
   * @default 1
   * */
  rows?: number;
}

export const HeadlessRichTextEditor = forwardRef<HTMLDivElement, HeadlessRichTextEditorProps>(
  (props: Readonly<HeadlessRichTextEditorProps>, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      value = '',
      onChange,
      controls,
      placeholder,
      onImageUpload,
      error,
      loading,
      rows = 1,
      onBlur,
    } = props;

    const ui = useRteUi();
    const contentRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);

    const ContentComponent = ui.components.Content;
    const RootComponent = ui.components.Root;
    const ToolbarComponent = ui.components.Toolbar;
    const LoadingOverlay = ui.components.LoadingOverlay;

    return (
      <RichTextEditorProvider
        onImageUpload={onImageUpload}
        placeholder={placeholder}
        content={value}
        controls={controls}
        onChange={onChange}
      >
        <RootComponent error={!!error}>
          {loading && LoadingOverlay ? <LoadingOverlay /> : null}
          <ToolbarComponent />
          <ContentComponent rows={rows} ref={contentRef} onBlur={onBlur} />
        </RootComponent>
      </RichTextEditorProvider>
    );
  },
);

HeadlessRichTextEditor.displayName = 'HeadlessRichTextEditor';

export default HeadlessRichTextEditor;
